import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ME_QUERY_KEY } from './api.js';

/**
 * The API answers 403 `PASSWORD_CHANGE_REQUIRED` to every route but the change-password flow while
 * the person holds an HR-issued temporary password. If that happens mid-session (HR issued a
 * temporary password while the user was signed in), re-fetch `me` so AppShell's blocking step
 * appears. Guarded so it cannot loop: once `me` already says the flag is set, AppShell renders no
 * routed content and nothing else queries.
 */
function refetchSessionOnPasswordChangeRequired(error: unknown): void {
  const e = error as { status?: number; body?: { code?: string } } | undefined;
  if (e?.status !== 403 || e.body?.code !== 'PASSWORD_CHANGE_REQUIRED') return;
  const me = queryClient.getQueryData<{ body?: { person?: { mustChangePassword?: boolean } } }>(
    ME_QUERY_KEY,
  );
  if (me?.body?.person?.mustChangePassword === true) return;
  void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}

/**
 * G-2: attendance figures are derived on the server from punches, regularisations, leave, shifts and rosters, so
 * almost any successful write can change them (a punch, an approval, a leave, a shift assignment). Rather than each
 * screen remembering which keys to drop, every successful mutation marks the attendance queries stale. Only the
 * queries currently on screen refetch; the rest just reload next time they are shown. Every attendance query key
 * starts with 'attendance' (or 'my-effective-shift'), which is what makes this one line enough.
 */
function refreshAttendanceAfterWrite(): void {
  void queryClient.invalidateQueries({ queryKey: ['attendance'] });
  void queryClient.invalidateQueries({ queryKey: ['my-effective-shift'] });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: refetchSessionOnPasswordChangeRequired }),
  mutationCache: new MutationCache({
    onError: refetchSessionOnPasswordChangeRequired,
    onSuccess: refreshAttendanceAfterWrite,
  }),
  defaultOptions: {
    queries: {
      // HR data changes on human timescales, not machine ones: a directory or a leave balance is
      // not worth refetching on every window focus. This cuts request volume substantially
      // without anyone noticing staleness.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // Never retry a 4xx — a 403 or a validation failure will fail identically three more times
      // and only delays the error the user needs to see.
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});
